// @flow
import moment from 'moment'
import { STEP_TYPE } from '../../../constants/stepType'
import buildWalkingStep from '../../trips/builders/walkSearchStep'
import appendWalkSteps from '../../trips/builders/appendWalkSteps'
import buildAmenityItem from '../../trips/builders/tripAmenity'
import buildServiceHours from '../../trips/builders/serviceHours'
import buildSearchPrice from '../../trips/builders/tripPrice'
import buildDetailedLocation from '../../trips/builders/detailedLocation'
import buildVoyageStationLocation from './voyageStationLocation'
import { buildTicketTypes } from '../../trips/builders/prebookSearchStep'
import {
  isCancelationFree,
  isBookable,
  getAccurateTime,
} from '../../../models/step'
import { DATETIME_FORMAT } from '../../../constants/date'
import { config } from '../../../config'

// Types
import type { Reservation } from '../../../models/reservation'
import type { Step, TicketType, CancellationPolicy } from '../../../models/step'
import type { PrebookReservation, VoyageTicketData } from '../../../api_types'

/**
 * Parse given datetime with support of both old and
 * new (ISO) formats
 *
 * @param {String} dateStr
 * @return {moment}
 */
function parseAnyDatetime(dateStr) {
  if (!dateStr) return null
  if (dateStr.indexOf(' ') > 0) {
    return moment.parseZone(dateStr, DATETIME_FORMAT)
  }
  return moment.parseZone(dateStr)
}

/**
 * Returns ticket types array from existin ticked data in the reservation
 * @type {Function}
 */
const buildTicketFromData = (ticketData: ?VoyageTicketData): ?TicketType => {
  if (
    !ticketData ||
    !ticketData.arrival_datetime ||
    !ticketData.departure_datetime
  ) {
    return null
  }

  const arrival = parseAnyDatetime(ticketData.arrival_datetime)
  const departure = parseAnyDatetime(ticketData.departure_datetime)

  const ticketType = {
    isDefault: true,
    label: '',
    tooltip: '',
    defaultScheduleId: null,
    ticketId: ticketData.id,
    ticketName: ticketData.name,
    validity: ticketData.validity || '',
    validUntil: moment.parseZone(ticketData.valid_until),
    validUntilForHumans: moment.parseZone(ticketData.valid_until_for_humans),
    count: 1,
    price: null,
    lowestPrice: null,
    schedules: [
      {
        arrivalDatetime: arrival,
        departureDatetime: departure,
        transfers: parseInt(ticketData.transfers || '0', 10),
        duration: {
          value: arrival ? arrival.diff(departure, 'minutes') : 0,
        },
        price: {
          compact: '...',
          display: '...',
          value: 0,
        },
      },
    ],
  }

  return ticketType
}

/**
 * Build ticket_types and pass_types fields of the step
 * @param  {PrebookVoyage} voyage
 * @return {Object}
 */
function buildTickets(voyage: *) {
  const ticketTypes = buildTicketTypes(voyage.ticket_types)
  const passTypes = buildTicketTypes(voyage.pass_types)

  // From old "ticket_data" field
  if (voyage.ticket_data) {
    const ticket = buildTicketFromData(voyage.ticket_data)
    if (ticket) {
      ticketTypes.push(ticket)
    }
  }

  return { ticketTypes, passTypes }
}

/**
 * Returns a cancellation policy object
 * @param  {Array} policy Policy
 * @return {Array} Formatted policy
 * @private
 */
const buildCancellationPolicy = (policy): Array<CancellationPolicy> =>
  policy.map((item) => ({
    notice: item.notice,
    refundPercent: item.refund_percent,
  }))

/**
 * By given voyage and already built main step try to make all other steps
 * from the `voyage.steps`
 * @param  {Object} voyage
 * @param  {Object} builtMainStep
 * @return {Step[]}
 */
function buildOtherSteps(voyage, builtMainStep) {
  const mainStepIndex = voyage.steps && voyage.steps.findIndex((s) => s.main)
  if (!voyage.steps || mainStepIndex <= 0) return [builtMainStep]

  const otherSteps = voyage.steps.map((step) => {
    // Ignore main step because we built it already
    if (!step.main) {
      if (step.step_type === STEP_TYPE.WALK) {
        return buildWalkingStep(step)
      }
    }
    return null
  })

  // Replace main step with already built one
  otherSteps[mainStepIndex] = builtMainStep
  return otherSteps.filter(Boolean)
}

/**
 * Build a voyage object
 * @param {Object} step Voyage information
 * @param {Object} provides Provider info
 * @return {Object} Voyage object
 * @private
 */
function buildSteps(trip: PrebookReservation): Step[] {
  const { voyage } = trip
  const mainStep = voyage.steps && voyage.steps.find((s) => s.main)
  const accurateTime = (mainStep && mainStep.details.time) || 0
  const { passTypes, ticketTypes } = buildTickets(voyage)
  const amenities = !voyage.amenities
    ? []
    : voyage.amenities.map(buildAmenityItem).filter(config.AMENITIES_FILTER)

  const builtMainStep = {
    passTypes,
    ticketTypes,
    from: {
      location: voyage.start_location
        ? buildDetailedLocation(voyage.start_location)
        : null,
      distance: voyage.start_difference,
      station:
        (mainStep &&
          mainStep.details.pickup_station &&
          buildVoyageStationLocation(mainStep.details.pickup_station)) ||
        null,
    },
    to: {
      location: voyage.end_location
        ? buildDetailedLocation(voyage.end_location)
        : null,
      distance: voyage.end_difference,
      station:
        (mainStep &&
          mainStep.details.dropoff_station &&
          buildVoyageStationLocation(mainStep.details.dropoff_station)) ||
        null,
    },
    description: (mainStep && mainStep.details.description) || '',
    main: true,
    airline: trip.airline || '',
    flight: trip.flight_number || '',
    cruise: trip.cruise_line || '',
    ship: trip.ship_name || '',
    alternativeTimes: voyage.alternative_times,
    alternativeTimesIndex: voyage.alternative_time_index || 0,
    arrivalDatetime: parseAnyDatetime(voyage.arrival_datetime),
    type:
      voyage.vehicle.category === 'walk' ? 'walking' : voyage.vehicle.category,
    departureDatetime: parseAnyDatetime(voyage.departure_datetime),
    bookingDetails: {
      startAddressIncomplete: !!voyage.booking_details.start_address_incomplete,
      endAddressIncomplete: !!voyage.booking_details.end_address_incomplete,
      showTollsAndGratuity: Boolean(!voyage.booking_details.hide_options),
      showFlightInfo: Boolean(!voyage.booking_details.ignore_flight_info),
      termsOfServiceURL: voyage.booking_details.terms_url || '',
      gratuityIncluded: Boolean(voyage.booking_details.gratuity_included),
      acceptsGratuity: Boolean(voyage.booking_details.accepts_gratuity),
      meetAndGreetDefault: Boolean(
        voyage.booking_details.meet_and_greet_default
      ),
      meetAndGreetAvailable: Boolean(
        voyage.booking_details.meet_and_greet_available
      ),
      meetAndGreetPrice: voyage.booking_details.meet_and_greet_price || '',
      meetAndGreetRawPrice: parseFloat(
        voyage.booking_details.meet_and_greet_raw_price || '0'
      ),
      cancellationPolicy: buildCancellationPolicy(
        voyage.booking_details.cancellation_policy
      ),
      cancellableOffline: Boolean(voyage.booking_details.cancellable_offline),
      cancellableOnline: Boolean(voyage.booking_details.cancellable_online),
      amendable: Boolean(voyage.booking_details.amendable),
      tollsIncluded: Boolean(voyage.booking_details.tolls_included),
      isBookable: Boolean(voyage.booking_details.bookable),
      waitTime: voyage.booking_details.wait_time,
      startTime: voyage.booking_details.start_time,
      endTime: voyage.booking_details.end_time,
      notes: voyage.booking_details.notes || '',
      extraPaxRequired: Boolean(voyage.booking_details.extra_pax_required),
      hourlyDetails: !voyage.hourly_details
        ? null
        : {
            includedKilometersPerHour:
              voyage.hourly_details.included_kilometers_per_hour,
            totalIncludedKilometers:
              voyage.hourly_details.total_included_kilometers,
            hoursRequested: voyage.hourly_details.hours_requested,
            minimum: voyage.hourly_details.minimum,
            originalRequested: voyage.hourly_details.original_requested,
          },
      areTermsInternal: false,
      specialInstructionsPlaceholder: '',
    },
    vehicle: {
      type: {
        id: voyage.vehicle.key,
        key: voyage.vehicle.type,
        name: voyage.vehicle.type,
      },
      numVehicles: voyage.vehicle.num_vehicles || 1,
      maxPassengers: voyage.vehicle.max_passengers,
      maxBags: voyage.vehicle.max_bags,
      isMaxBagsPerPerson: voyage.vehicle.is_max_bags_per_person,
      totalBags: voyage.vehicle.total_bags,
      imageUrl: voyage.vehicle.image_url || trip.provider.logo_url || '',
      make: voyage.vehicle.make || '',
      model: voyage.vehicle.model || '',
      vehicleClass: voyage.vehicle.vehicle_class_detail?.vehicle_class_id, // eslint-disable-line camelcase
      vehicleClassName: voyage.vehicle.vehicle_class_detail?.display_name || '', // eslint-disable-line camelcase
    },
    provider: {
      name: voyage.provider_name || '',
      realName: voyage.provider_name || '',
      logoUrl: trip.provider.logo_url || voyage.vehicle.image_url || '',
      phoneNumber: trip.provider.phone_number,
      email: trip.provider.email,
      rating: trip.provider.rating,
      ratingFloat: trip.provider.rating, // missing in response
      ratingCount: 0, // missing in respinse
      supplierScore: -1,
    },
    serviceHours: buildServiceHours(voyage.booking_details),
    passengersCount: voyage.num_passengers,
    amenities,
    accurateTime,
  }

  return buildOtherSteps(voyage, builtMainStep)
}

/**
 * Builds an "extra trip offer" - used to offer rides from the _other_ airport
 * i.e. the airport for which this reservation was not booked
 * @param {PrebookReservation} trip PrebookReservation information
 * @return {Object} the extra trip offer object (or null if one is not available)
 * @private
 */
function buildExtraTripOffer(trip: PrebookReservation) {
  // eslint-disable-next-line camelcase
  const extraTripOffer = trip.offers?.extra_trip_offer
  if (!extraTripOffer) return null

  return {
    iataCode: extraTripOffer.extra_trip_iata_code,
    direction: extraTripOffer.trip_direction,
    searchLink: extraTripOffer.extra_trip_search_link,
    couponCode: extraTripOffer.extra_trip_coupon_code,
    couponPercentage: extraTripOffer.extra_trip_coupon_percentage,
  }
}

/**
 * Returns a Reservation object
 * @param  {Object} reservation Reservation response
 * @return {Object} Parsed reservation object
 * @private
 */
function reservationBuilder(reservation: PrebookReservation): Reservation {
  const steps = appendWalkSteps(buildSteps(reservation))
  const { voyage } = reservation
  const summDuration = getAccurateTime(steps)
  const priceParsed = buildSearchPrice(
    { price: { total_price: reservation.total_price } },
    { code: reservation.currency, sign: '' }
  )

  return {
    trip: {
      id: reservation.id,
      searchId: '',
      duration: summDuration,
      type: 'prebook',
      createdAt: parseAnyDatetime(reservation.timestamp),
      expiresIn: +Infinity,
      expiresAt: +Infinity,
      price: priceParsed,
      totalPrice: priceParsed.raw,
      tags: [],
      isEstimate: null,
      preferred: false,
      preferredByCompany: false,
      recommended: false,
      freeCancellation: isCancelationFree(steps),
      taxesAndFeesIncluded: isBookable(steps),
      outOfPolicy: false,
      fltSupport: !!voyage.flt_support,
      vehiclesIncluded: [],
      rewardPoints: reservation.loyalty?.points || 0,
      rewardProgram: reservation.loyalty?.program,
      rewardProgramDisplay: reservation.loyalty?.display_name, // eslint-disable-line camelcase
      steps,
      startLocation:
        voyage.start_location && buildDetailedLocation(voyage.start_location),
      endLocation:
        voyage.end_location && buildDetailedLocation(voyage.end_location),
      numPassengers: voyage.num_passengers,
      returnSteps: [],
      flightDatetime: voyage.flight_datetime
        ? parseAnyDatetime(voyage.flight_datetime)
        : null,
      pickupDatetime: voyage.departure_datetime
        ? parseAnyDatetime(voyage.departure_datetime)
        : null,
      flightType: reservation.flight_type || '',
      returnFlightDatetime: null,
      returnPickupDatetime: null,
      discountPercentage: null,
      confirmationNumber: reservation.confirmation_number,
      cancelled: Boolean(reservation.cancelled),
      specialInstructions: reservation.customer_special_instructions || '',
      extraPaxInfo: (reservation.extra_pax_info || []).map((x) => ({
        firstName: x.first_name,
        lastName: x.last_name,
        email: x.email,
      })),
      owner: {
        firstName: reservation.first_name,
        lastName: reservation.last_name,
        phoneNumber: `${reservation.phone_number_national || ''}`,
        countryCodeName: reservation.phone_number_country_code_name,
        email: reservation.email,
      },
      couponCode: reservation.coupon_code,
      hashedCustomerEmail: reservation.hashed_customer_email,
    },
    canCancel: Boolean(reservation.can_cancel),
    cancellationDate: reservation.cancelled_timestamp
      ? parseAnyDatetime(reservation.cancelled_timestamp)
      : null,
    rebookedId: reservation.rebooked_id,
    pickupInstructions: reservation.pickup_instructions || '',
    pickupTime: parseAnyDatetime(reservation.voyage.departure_datetime),
    amountPaid: reservation.amount_paid,
    amendable: Boolean(reservation.voyage.booking_details.amendable),
    cancellableOffline: Boolean(
      reservation.voyage.booking_details.cancellable_offline
    ),
    cancellableOnline: Boolean(
      reservation.voyage.booking_details.cancellable_online
    ),
    refundPercent: reservation.voyage.booking_details.cancellation_policy.length
      ? reservation.voyage.booking_details.cancellation_policy[0].refund_percent
      : 0,
    meetAndGreetAvailable: Boolean(
      reservation.voyage.booking_details.meet_and_greet_default
    ),
    cancellationPolicyNotice: reservation.voyage.booking_details
      .cancellation_policy.length
      ? reservation.voyage.booking_details.cancellation_policy[0].notice
      : 0,
    cancellationPenaltyPrice: null,
    mozioProfitUsd: reservation.mozio_profit_usd,
    partnerProfitUsd: reservation.partner_profit_usd,
    grossRevenueUsd: reservation.gross_revenue_usd,
    mozioProfit: reservation.mozio_profit,
    partnerProfit: reservation.partner_profit,
    grossRevenue: reservation.gross_revenue,
    extraTripOffer: buildExtraTripOffer(reservation),
    userType: reservation.user_type,
    partnerTrackingId: reservation.partner_tracking_id,
    initiatedBy: reservation.user_first_name
      ? {
          firstName: reservation.user_first_name ?? '',
          lastName: reservation.user_last_name ?? '',
          email: reservation.user_email ?? '',
        }
      : null,
  }
}

export default reservationBuilder
